"use client"

import { Collapsible } from "@base-ui/react"
import {
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/16/solid"
import { useSystemStatusViewed } from "@src/hooks/useSystemStatusViewed"
import { useSystemStatus } from "@src/providers/SystemStatusProvider"
import { APP_NETWORK_OUTAGE_NOTIFICATION } from "@src/utils/environment"
import clsx from "clsx"

const SYSTEM_STATUS_URL = "https://status.near-intents.org/posts/dashboard"

function SystemStatusDesktop() {
  const statuses = useSystemStatus()
  const { viewed, markViewed } = useSystemStatusViewed()
  const statusType = statuses[0]?.status ?? null
  const externalMessages = [
    ...new Set(statuses.map((status) => status.message)),
  ]
  const messages = APP_NETWORK_OUTAGE_NOTIFICATION
    ? [...externalMessages, APP_NETWORK_OUTAGE_NOTIFICATION]
    : externalMessages

  if (messages.length === 0) return null

  const label =
    statusType === "incident"
      ? "Incident detected"
      : statusType === "maintenance"
        ? "Maintenance in progress"
        : "Service notice"

  return (
    <Collapsible.Root
      onOpenChange={(isOpen) => {
        if (isOpen) markViewed()
      }}
      className={clsx(
        "-outline-offset-1 flex flex-col overflow-hidden rounded-2xl outline",
        statusType === "incident"
          ? "bg-red-100 confidential:bg-red-500/10 outline-red-500/7"
          : "bg-yellow-200/50 confidential:bg-yellow-500/10 outline-yellow-500/10"
      )}
    >
      <Collapsible.Trigger
        className={clsx(
          "group flex items-center justify-between gap-3 rounded-2xl px-3.5 py-3 text-left",
          statusType === "incident"
            ? "confidential:text-red-300 text-red-800"
            : "confidential:text-yellow-300 text-yellow-800"
        )}
      >
        <span className="relative flex size-4 shrink-0 items-center justify-center">
          {!viewed && (
            <>
              <span
                className={clsx(
                  "pointer-events-none absolute inset-0 m-auto size-4 animate-ping-slow select-none rounded-full",
                  statusType === "incident" ? "bg-red-500" : "bg-yellow-500"
                )}
              />
              <span className="absolute flex size-4 items-center justify-center p-1">
                <span
                  className={clsx(
                    "size-full rounded-full",
                    statusType === "incident"
                      ? "bg-red-100 confidential:bg-red-900"
                      : "bg-yellow-100 confidential:bg-yellow-800"
                  )}
                />
              </span>
            </>
          )}
          <ExclamationCircleIcon className="relative size-4" />
        </span>
        <h4 className="flex-1 font-semibold text-sm/5">{label}</h4>
        <ChevronDownIcon className="size-5 shrink-0 group-data-panel-open:rotate-180" />
      </Collapsible.Trigger>

      <Collapsible.Panel
        className={clsx(
          "pr-3.5 pb-3 pl-10.5 font-semibold text-sm/5",
          statusType === "incident"
            ? "confidential:text-red-200 text-red-700"
            : "confidential:text-yellow-100 text-yellow-700"
        )}
      >
        {messages.length === 1 ? (
          <p>{messages[0]}</p>
        ) : (
          <ul className="list-disc space-y-2.5">
            {messages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        )}

        <a
          href={SYSTEM_STATUS_URL}
          target="_blank"
          rel="noreferrer noopener"
          className="mt-2 inline-flex items-center gap-1 whitespace-nowrap underline hover:decoration-2"
        >
          <span>View status</span>
          <ArrowTopRightOnSquareIcon className="size-4 shrink-0" />
        </a>
      </Collapsible.Panel>
    </Collapsible.Root>
  )
}

export default SystemStatusDesktop
